import { onUnhandledMutationError } from "@khazzin/api-client/query";
import * as RadixToast from "@radix-ui/react-toast";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { errorMessage } from "../errors";
import { Icon } from "../icons/Icon";
import { cx } from "../styles";
import { useUiText } from "./uiText";

/**
 * Transient confirmations and failures, bottom inline-end.
 *
 * Three rules, and each is a decision about attention rather than about
 * styling.
 *
 * **An error stays longer.** A success that vanishes after five seconds is
 * fine — the thing happened, and the screen behind already shows it. An error
 * stays ten seconds (owner, 2026-10-17), or until it is dismissed — which is
 * why every toast takes the pointer even over a modal drawer (ADJ-1016-8).
 *
 * **At most three.** A stack that grows without limit covers the screen it is
 * reporting on, and by the fourth nobody is reading them. When a fourth
 * arrives the oldest *non-error* one is dropped — errors are never evicted to
 * make room for a success, because the success is the less important message by
 * construction.
 *
 * **`role` follows severity.** A success is `status`, announced politely when
 * the screen reader finishes its sentence. An error is `alert`, announced
 * immediately, because it may be the only thing telling somebody their work did
 * not save.
 */

export type ToastTone = "success" | "error" | "info";

export interface ToastOptions {
	title: string;
	/** A second line: which record, which field, what to do next. */
	description?: string;
	/** One action, usually Undo or Retry. */
	action?: { label: string; onClick: () => void };
	/**
	 * How long before it disappears, in milliseconds.
	 *
	 * Defaults to [TOAST_DURATION], or [ERROR_TOAST_DURATION] for an error.
	 * Pass `0` to make any toast persist.
	 */
	duration?: number;
}

interface ToastRecord extends ToastOptions {
	id: number;
	tone: ToastTone;
}

/** The default lifetime of a toast that is not an error. */
export const TOAST_DURATION = 5_000;

/** The default lifetime of an error. */
export const ERROR_TOAST_DURATION = 10_000;

/** How many can stand at once. */
export const TOAST_LIMIT = 3;

export interface ToastApi {
	success: (options: ToastOptions) => void;
	error: (options: ToastOptions) => void;
	info: (options: ToastOptions) => void;
	/** Removes one by id. Mostly internal; the close button uses it. */
	dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Raises toasts. Throws outside the provider, which is a wiring bug. */
export function useToast(): ToastApi {
	const api = useContext(ToastContext);
	if (api === null) {
		throw new Error("useToast must be used inside a ToastProvider");
	}
	return api;
}

export interface ToastProviderProps {
	children: ReactNode;
	/** The label on every close button. */
	closeLabel?: string;
	/** Names the region for assistive technology. */
	label?: string;
}

export function ToastProvider({
	children,
	closeLabel = "Dismiss",
	label = "Notifications",
}: ToastProviderProps) {
	const [toasts, setToasts] = useState<ToastRecord[]>([]);
	// A counter rather than a timestamp: two toasts raised in the same
	// millisecond would collide on a clock, and React would reuse one's DOM node
	// for the other.
	const nextId = useRef(0);

	const dismiss = useCallback((id: number) => {
		setToasts((current) => current.filter((toast) => toast.id !== id));
	}, []);

	const raise = useCallback((tone: ToastTone, options: ToastOptions) => {
		setToasts((current) => {
			const id = nextId.current++;
			const next = [...current, { ...options, tone, id }];

			if (next.length <= TOAST_LIMIT) {
				return next;
			}

			// Over the limit. Drop the oldest thing that was going to disappear on
			// its own anyway; if they are all errors, drop nothing and let them
			// stack — an error nobody has read is worse than a crowded corner.
			const oldestTransient = next.findIndex((toast) => toast.tone !== "error");
			return oldestTransient === -1
				? next
				: [...next.slice(0, oldestTransient), ...next.slice(oldestTransient + 1)];
		});
	}, []);

	// How many errors have been raised, so the fallback below can tell whether
	// the screen already said something.
	const errorsRaised = useRef(0);
	const api = useMemo<ToastApi>(
		() => ({
			success: (options) => raise("success", options),
			error: (options) => {
				errorsRaised.current++;
				raise("error", options);
			},
			info: (options) => raise("info", options),
			dismiss,
		}),
		[raise, dismiss],
	);

	// The fallback for a failed mutation nobody handled (ADJ-M03-1). It waits a
	// tick first: a caller that awaited `mutateAsync` and toasts in its own
	// `catch` does so before the timer fires, and then this stays quiet rather
	// than saying the same thing twice.
	const t = useUiText();
	useEffect(
		() =>
			onUnhandledMutationError((error) => {
				const before = errorsRaised.current;
				window.setTimeout(() => {
					if (errorsRaised.current === before) {
						api.error({ title: errorMessage(error, t) });
					}
				}, 0);
			}),
		[api, t],
	);

	return (
		<ToastContext.Provider value={api}>
			<RadixToast.Provider label={label} swipeDirection="right">
				{children}
				{toasts.map((toast) => (
					<ToastItem
						key={toast.id}
						toast={toast}
						closeLabel={closeLabel}
						onDismiss={() => dismiss(toast.id)}
					/>
				))}
				<RadixToast.Viewport
					// While a drawer is open the stack rises above its footer
					// (index.css, ADJ-1016-8): the drawer sits in the same corner,
					// and a toast over Save is a toast over the one button that
					// matters.
					data-toast-viewport=""
					className={cx(
						// Bottom inline-end: the corner furthest from where somebody is
						// reading, and the corner that is a different one in Arabic.
						"fixed bottom-4 end-4 z-[var(--z-toast)]",
						"flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none",
					)}
				/>
			</RadixToast.Provider>
		</ToastContext.Provider>
	);
}

const TONES: Record<ToastTone, { border: string; icon: typeof Info; colour: string }> = {
	success: { border: "border-success-border", icon: CheckCircle2, colour: "text-success-fg" },
	error: { border: "border-danger-border", icon: AlertCircle, colour: "text-danger-fg" },
	info: { border: "border-info-border", icon: Info, colour: "text-info-fg" },
};

function ToastItem({
	toast,
	closeLabel,
	onDismiss,
}: {
	toast: ToastRecord;
	closeLabel: string;
	onDismiss: () => void;
}) {
	const tone = TONES[toast.tone];
	const lifetime =
		toast.duration ?? (toast.tone === "error" ? ERROR_TOAST_DURATION : TOAST_DURATION);

	return (
		<RadixToast.Root
			// The role is set explicitly, and it is the part that matters: Radix
			// leaves the toast element itself unlabelled and announces through a
			// hidden live region instead, which means nothing on the visible
			// element says what it is. `alert` interrupts the screen reader;
			// `status` waits its turn. A failed save is worth interrupting for —
			// it may be the only thing telling somebody their work is gone — and a
			// successful one is not.
			role={toast.tone === "error" ? "alert" : "status"}
			// Radix's own hint, which also decides whether the toast joins the F8
			// hotkey list. Kept in step with the role above.
			type={toast.tone === "error" ? "foreground" : "background"}
			// A modal drawer or dialog sets `pointer-events: none` on <body>, and
			// Radix gives every layer opened before it `none` as well — so an
			// error that was already standing when a drawer opened could not be
			// closed or swiped until the drawer shut (ADJ-1016-8). The viewport is
			// a dismissable-layer branch, so a click here never closes the drawer.
			// Inline, overriding Radix's own inline value.
			style={{ pointerEvents: "auto" }}
			// Radix treats Infinity as "never", which is what `0` asks for.
			duration={lifetime === 0 ? Number.POSITIVE_INFINITY : lifetime}
			onOpenChange={(open) => {
				if (!open) {
					onDismiss();
				}
			}}
			className={cx(
				"flex items-start gap-2.5 rounded-sm border bg-bg-surface p-3 shadow-overlay",
				tone.border,
				// The 3px bar at the inline start, from the design — a second signal
				// beside the icon, for anybody who cannot tell the colours apart.
				"border-s-[3px]",
				toast.tone === "success" && "border-s-success-fg",
				toast.tone === "error" && "border-s-danger-fg",
				toast.tone === "info" && "border-s-info-fg",
				"data-[state=open]:animate-[km-rise_150ms_ease-out] motion-reduce:!animate-none",
			)}
		>
			<Icon as={tone.icon} size={19} className={cx("mt-px shrink-0", tone.colour)} />

			<div className="min-w-0 flex-1">
				<RadixToast.Title className="text-[13px] font-bold text-fg">{toast.title}</RadixToast.Title>
				{toast.description !== undefined && (
					<RadixToast.Description className="mt-0.5 text-[11.5px] text-fg-subtle">
						{toast.description}
					</RadixToast.Description>
				)}
				{toast.action !== undefined && (
					<RadixToast.Action
						asChild
						// What a screen reader offers when it cannot render the button —
						// Radix requires it, and it is the answer to "what else can I do".
						altText={toast.action.label}
					>
						<button
							type="button"
							onClick={toast.action.onClick}
							className="mt-1.5 cursor-pointer text-[12px] font-bold text-brand-ink underline"
						>
							{toast.action.label}
						</button>
					</RadixToast.Action>
				)}
			</div>

			<RadixToast.Close
				aria-label={closeLabel}
				className="shrink-0 cursor-pointer text-fg-subtle hover:text-fg"
			>
				<Icon as={X} size={17} />
			</RadixToast.Close>
		</RadixToast.Root>
	);
}
